// src/components/panel/BrandsStatus.js
import React, { useState, useEffect } from 'react';
import { TagOutlined, CheckCircleOutlined, WarningOutlined, CloseCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import { Table, Card, Row, Col, Statistic, Spin, Tag, Button, message, Progress, Collapse } from 'antd';
import axios from 'axios';
import { NODE_BACKEND_API_URL } from '../../config';
import './BrandsStatus.css';

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';
// In demo mode, "today" is Jan 13 2026 so staleness is calculated relative to that date
const DEMO_TODAY = new Date('2026-01-13T23:59:59.000Z');

function BrandsStatus() {
    const [brandData, setBrandData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [downloadStatus, setDownloadStatus] = useState('idle'); // idle, requesting, processing, completed, failed
    const [error, setError] = useState(null);

    useEffect(() => {
        loadBrands();
    }, []);

    const loadBrands = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${NODE_BACKEND_API_URL}/getbrandquantities`);
            setBrandData(response.data || []);
            setError(null);
        } catch (err) {
            console.error('Error loading brands:', err);
            setError('Error al cargar marcas');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadMasterList = async () => {
        if (isDemoMode) {
            message.success('✅ Lista Descargada para uso Offline', 3);
            return;
        }
        try {
            setDownloading(true);
            setDownloadStatus('requesting');
            setDownloadProgress(0);
            message.loading('Iniciando generación de lista maestra...', 0);
            
            const token = localStorage.getItem('token');
            
            // Step 1: Request generation
            const requestResponse = await axios.get(`${NODE_BACKEND_API_URL}/master-list/request`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            const { jobId } = requestResponse.data;
            
            if (!jobId) {
                throw new Error('No se recibió ID de trabajo');
            }
            
            message.destroy();
            message.loading('Generando archivo Excel...', 0);
            setDownloadStatus('processing');
            
            // Step 2: Poll status
            await pollDownloadStatus(jobId, token);
            
        } catch (err) {
            message.destroy();
            console.error('Error downloading master list:', err);
            setDownloadStatus('failed');
            
            if (err.response?.status === 401) {
                message.error('Sesión expirada. Por favor, inicie sesión nuevamente.');
            } else if (err.response?.status === 500) {
                message.error('Error en el servidor. Por favor, intente más tarde.');
            } else {
                message.error('Error al generar la lista maestra');
            }
        } finally {
            setDownloading(false);
        }
    };

    const pollDownloadStatus = async (jobId, token) => {
        const maxAttempts = 60; // 2 minutes max (60 * 2 seconds)
        let attempts = 0;
        
        const checkStatus = async () => {
            try {
                attempts++;
                
                const statusResponse = await axios.get(
                    `${NODE_BACKEND_API_URL}/master-list/status/${jobId}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    }
                );
                
                const { status, progress, error: jobError } = statusResponse.data;
                
                // Update progress
                setDownloadProgress(progress || 0);
                
                if (status === 'completed') {
                    // Step 3: Download file
                    message.destroy();
                    message.loading('Descargando archivo...', 0);
                    await downloadFile(jobId, token);
                    
                    message.destroy();
                    message.success('Lista maestra descargada correctamente');
                    setDownloadStatus('completed');
                    
                    // Reset after 2 seconds
                    setTimeout(() => {
                        setDownloadStatus('idle');
                        setDownloadProgress(0);
                    }, 2000);
                    
                } else if (status === 'failed') {
                    message.destroy();
                    throw new Error(jobError || 'Generación fallida');
                    
                } else if (attempts >= maxAttempts) {
                    message.destroy();
                    throw new Error('Tiempo de espera agotado');
                    
                } else {
                    // Continue polling every 2 seconds
                    setTimeout(checkStatus, 2000);
                }
                
            } catch (err) {
                message.destroy();
                console.error('Error checking status:', err);
                setDownloadStatus('failed');
                throw err;
            }
        };
        
        await checkStatus();
    };

    const downloadFile = async (jobId, token) => {
        try {
            const response = await axios.get(
                `${NODE_BACKEND_API_URL}/master-list/download/${jobId}`,
                {
                    responseType: 'blob',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            
            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            
            // Extract filename from response headers or use default
            const contentDisposition = response.headers['content-disposition'];
            const filename = contentDisposition
                ? contentDisposition.split('filename=')[1].replace(/"/g, '')
                : `Lista_Maestra_${new Date().toISOString().split('T')[0]}.xlsx`;
            
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
        } catch (err) {
            console.error('Error downloading file:', err);
            throw new Error('Error al descargar el archivo');
        }
    };

    // Function to get update status based on date difference
    const getUpdateStatus = (dateString) => {
        if (!dateString) return 'critical';
        
        const lastUpdate = new Date(dateString);
        const currentDate = isDemoMode ? DEMO_TODAY : new Date();
        const diffTime = Math.abs(currentDate - lastUpdate);
        const diffMonths = diffTime / (1000 * 60 * 60 * 24 * 30.44);
        
        if (diffMonths > 3) return 'critical'; // Red
        if (diffMonths >= 1) return 'warning'; // Yellow
        return 'good'; // Green
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'Sin fecha';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-AR', { 
            day: '2-digit', 
            month: '2-digit', 
            year: '2-digit' 
        });
    };

    // Categorize brands by status
    const updatedBrands = brandData.filter(b => getUpdateStatus(b.LastUpdate) === 'good');
    const warningBrands = brandData.filter(b => getUpdateStatus(b.LastUpdate) === 'warning');
    const criticalBrands = brandData.filter(b => getUpdateStatus(b.LastUpdate) === 'critical');

    // Calculate totals
    const totalBrands = brandData.length;
    const totalProducts = brandData.reduce((sum, b) => sum + (parseInt(b.NumberOfProducts) || 0), 0);

    // Table columns
    const columns = [
        {
            title: 'Marca',
            dataIndex: 'BrandName',
            key: 'BrandName',
            sorter: (a, b) => (a.BrandName || '').localeCompare(b.BrandName || ''),
            ellipsis: true,
        },
        {
            title: 'Productos',
            dataIndex: 'NumberOfProducts',
            key: 'NumberOfProducts',
            sorter: (a, b) => (a.NumberOfProducts || 0) - (b.NumberOfProducts || 0),
            align: 'center',
            width: 120,
        },
        {
            title: 'Actualizado',
            dataIndex: 'LastUpdate',
            key: 'LastUpdate',
            render: (date) => formatDate(date),
            sorter: (a, b) => {
                const dateA = a.LastUpdate ? new Date(a.LastUpdate) : new Date(0);
                const dateB = b.LastUpdate ? new Date(b.LastUpdate) : new Date(0);
                return dateA - dateB;
            },
            align: 'center',
            width: 120,
        },
    ];

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" tip="Cargando marcas..." />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ textAlign: 'center', padding: '50px', color: '#ff4d4f' }}>
                {error}
            </div>
        );
    }

    return (
        <div style={{ overflowX: 'hidden', width: '100%' }}>
            {/* Summary Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col xs={24} sm={12} md={6} lg={4}>
                    <Card>
                        <Statistic
                            title="Total Marcas"
                            value={totalBrands}
                            prefix={<TagOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6} lg={4}>
                    <Card>
                        <Statistic
                            title="Productos listados"
                            value={totalProducts}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6} lg={4}>
                    <Card>
                        <Statistic
                            title="Listas Actualizadas"
                            value={updatedBrands.length}
                            prefix={<CheckCircleOutlined />}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6} lg={4}>
                    <Card>
                        <Statistic
                            title="Requieren Atención"
                            value={warningBrands.length + criticalBrands.length}
                            prefix={<WarningOutlined />}
                            valueStyle={{ color: '#faad14' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={24} md={12} lg={8}>
                    <Card style={{ height: '100%', padding: '4px' }} bodyStyle={{ padding: '8px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={handleDownloadMasterList}
                            loading={downloading}
                            disabled={downloading}
                            size="large"
                            block
                            style={{ 
                                height: downloading ? 'auto' : '100%', 
                                minHeight: '60px',
                                backgroundColor: '#217346',
                                borderColor: '#217346'
                            }}
                        >
                            {downloading 
                                ? downloadStatus === 'requesting' 
                                    ? 'Iniciando...' 
                                    : `Generando... ${downloadProgress}%`
                                : 'Descargar Lista Maestra'
                            }
                        </Button>
                        {downloading && downloadStatus === 'processing' && (
                            <Progress 
                                percent={downloadProgress} 
                                size="small" 
                                status="active"
                                style={{ marginTop: '8px' }}
                            />
                        )}
                    </Card>
                </Col>
            </Row>

            {/* Collapsible Sections */}
            <Collapse 
                style={{ backgroundColor: 'transparent', border: 'none' }}
            >
                {/* Green - Updated Brands */}
                {updatedBrands.length > 0 && (
                    <Collapse.Panel
                        key="1"
                        header={
                            <span>
                                <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
                                Listas de Productos Actualizadas (Hace menos de 1 mes)
                                <Tag color="green" style={{ marginLeft: '8px' }}>{updatedBrands.length}</Tag>
                            </span>
                        }
                        style={{ marginBottom: '16px', overflow: 'hidden', backgroundColor: '#fff', borderRadius: '8px' }}
                        className="brands-collapse-panel-green"
                    >
                        <Table
                            columns={columns}
                            dataSource={updatedBrands}
                            rowKey="BrandName"
                            pagination={{ 
                                pageSize: 10, 
                                showSizeChanger: true, 
                                showTotal: (total) => `Total: ${total}`,
                                locale: { items_per_page: '/ Pág.' }
                            }}
                            size="small"
                            scroll={{ x: false }}
                        />
                    </Collapse.Panel>
                )}

                {/* Yellow - Warning Brands */}
                {warningBrands.length > 0 && (
                    <Collapse.Panel
                        key="2"
                        header={
                            <span>
                                <WarningOutlined style={{ color: '#faad14', marginRight: '8px' }} />
                                Listas de Productos Próximas a Vencer (Hace 1-3 meses)
                                <Tag color="gold" style={{ marginLeft: '8px' }}>{warningBrands.length}</Tag>
                            </span>
                        }
                        style={{ marginBottom: '16px', overflow: 'hidden', backgroundColor: '#fff', borderRadius: '8px' }}
                        className="brands-collapse-panel-yellow"
                    >
                        <Table
                            columns={columns}
                            dataSource={warningBrands}
                            rowKey="BrandName"
                            pagination={{ 
                                pageSize: 10, 
                                showSizeChanger: true, 
                                showTotal: (total) => `Total: ${total}`,
                                locale: { items_per_page: '/ Pág.' }
                            }}
                            size="small"
                            scroll={{ x: false }}
                        />
                    </Collapse.Panel>
                )}

                {/* Red - Critical Brands */}
                {criticalBrands.length > 0 && (
                    <Collapse.Panel
                        key="3"
                        header={
                            <span>
                                <CloseCircleOutlined style={{ color: '#ff4d4f', marginRight: '8px' }} />
                                Listas de Productos Desactualizadas (Hace más de 3 meses)
                                <Tag color="red" style={{ marginLeft: '8px' }}>{criticalBrands.length}</Tag>
                            </span>
                        }
                        style={{ overflow: 'hidden', backgroundColor: '#fff', borderRadius: '8px' }}
                        className="brands-collapse-panel-red"
                    >
                        <Table
                            columns={columns}
                            dataSource={criticalBrands}
                            rowKey="BrandName"
                            pagination={{ 
                                pageSize: 10, 
                                showSizeChanger: true, 
                                showTotal: (total) => `Total: ${total}`,
                                locale: { items_per_page: '/ Pág.' }
                            }}
                            size="small"
                            scroll={{ x: false }}
                        />
                    </Collapse.Panel>
                )}
            </Collapse>
        </div>
    );
}

export default BrandsStatus;
